// Backend timestamps come in two flavors:
//   - SQLite UTC audit rows: now ISO-tagged as "YYYY-MM-DDTHH:MM:SSZ".
//   - SQL Server naive datetimes: "YYYY-MM-DDTHH:MM:SS" (no TZ) – JS parses
//     these as local, which matches how they're already understood in-app.
// Safety net: if a raw "YYYY-MM-DD HH:MM:SS" ever slips through, treat it as
// UTC (that's what SQLite's `datetime('now')` returns).
function parse(input: string): Date | null {
  const normalized =
    input.includes("T") || /Z$|[+-]\d\d:?\d\d$/.test(input)
      ? input
      : input.replace(" ", "T") + "Z";
  const d = new Date(normalized);
  return Number.isNaN(d.getTime()) ? null : d;
}

const pad = (n: number) => String(n).padStart(2, "0");

export function formatDateOnly(iso: string | null | undefined): string {
  if (!iso) return "–";
  const d = parse(iso);
  if (!d) return iso.split("T")[0] ?? iso;
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return "–";
  const d = parse(iso);
  if (!d) return iso;
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

// YYYY-MM-DD in Asia/Kolkata. en-CA locale happens to emit that format.
export function todayIstIso(): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}

export function formatDurationHms(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return `${pad(h)}:${pad(m)}:${pad(sec)}`;
}

// Compact human duration ("4h 12m 07s", "12m 07s", "07s").
export function formatDurationCompact(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}h ${pad(m)}m ${pad(sec)}s`;
  if (m > 0) return `${m}m ${pad(sec)}s`;
  return `${sec}s`;
}

// Coarse duration for large totals ("142h 30m", "45m", "< 1m"). Seconds are
// noise once you're summing months of sessions, so they're dropped.
export function formatDurationHoursMinutes(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h > 0) return `${h}h ${pad(m)}m`;
  if (m > 0) return `${m}m`;
  return "< 1m";
}

// HH:MM:SS in local time (used for the compact "First login 09:14" summary).
export function formatTimeOnly(iso: string | null | undefined): string {
  if (!iso) return "–";
  const d = parse(iso);
  if (!d) return iso;
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}
